import { useState } from 'react';
import { library } from '@cwos/api-client';
import { Button } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { DraftWithPhibian } from './PhibianDrafts.js';
import { ProposeReading, type ReadingDraft } from './ProposeReading.js';
import { UploadSource } from './UploadSource.js';

/**
 * "+ ADD TO LIBRARY" — the one door into the Library, on every tab.
 *
 * Every way in ends in the SAME pipeline: a stored document, a reading a person
 * proposes from it, a review task, an approval. A rule typed by hand is stored
 * as a short document first, so it carries the same source, version,
 * provenance and approval record as one taken from a handbook.
 *
 *   Upload a document     → text read in the browser → pick passages, or let
 *                           Phibian draft readings citing them → propose
 *   Write a policy or rule → stored as its own document → proposed (Required)
 *   Organizational guidance → the same, proposed as Guidance
 *   Connect a source       → not built: no document connector exists yet
 */

type Step =
  | { kind: 'choose' }
  | { kind: 'upload' }
  | { kind: 'write'; flavour: 'rule' | 'guidance' }
  | { kind: 'passages'; source: library.SourceView; text: string };

export function AddToLibrary({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<Step>({ kind: 'choose' });

  if (step.kind === 'upload') {
    return (
      <UploadSource
        onUploaded={(source, text) => setStep({ kind: 'passages', source, text })}
        onCancel={() => setStep({ kind: 'choose' })}
      />
    );
  }
  if (step.kind === 'passages') {
    return <PickPassages source={step.source} text={step.text} onClose={onClose} />;
  }
  if (step.kind === 'write') {
    return (
      <WriteRule
        flavour={step.flavour}
        onBack={() => setStep({ kind: 'choose' })}
        onClose={onClose}
      />
    );
  }

  return (
    <section className="rn-card card-record rn-col kl-upload" aria-labelledby="kl-add-title">
      <div className="rn-card-head">
        <h2 className="rn-card-title" id="kl-add-title">
          Add to the Library
        </h2>
        <button type="button" className="rn-btn-quiet" onClick={onClose}>
          Close
        </button>
      </div>
      <p className="rn-muted">
        Whatever you add is stored and searchable at once. Nothing becomes a rule Phibian follows
        until an approver accepts it.
      </p>
      <div className="kl-meta-grid">
        <Option
          title="Upload a document"
          detail="A policy, handbook or procedure — PDF, Word, text or Markdown. Then pick the passages that are rules."
          onClick={() => setStep({ kind: 'upload' })}
        />
        <Option
          title="Write a policy or rule"
          detail="Type a rule your organization follows. It goes for review like any other."
          onClick={() => setStep({ kind: 'write', flavour: 'rule' })}
        />
        <Option
          title="Add organizational guidance"
          detail="Advice and good practice — what you expect, without making it a hard rule."
          onClick={() => setStep({ kind: 'write', flavour: 'guidance' })}
        />
        <Option
          title="Connect a document source"
          detail="Coming soon. Google Drive and SharePoint import are not available yet."
          disabled
        />
      </div>
    </section>
  );
}

function Option({
  title,
  detail,
  onClick,
  disabled = false,
}: {
  title: string;
  detail: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="rn-card rn-col"
      style={{ textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer' }}
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled}
    >
      <span className="rn-strong">{title}</span>
      <span className="rn-muted">{detail}</span>
    </button>
  );
}

/**
 * Step two of an upload: the document's own text, from which a person selects
 * the passages that are rules. The text is the one just extracted in this tab.
 */
function PickPassages({
  source,
  text,
  onClose,
}: {
  source: library.SourceView;
  text: string;
  onClose: () => void;
}) {
  const navigate = useNav();
  const [draft, setDraft] = useState<{ n: number; initial: ReadingDraft }>({ n: 0, initial: {} });
  const [proposed, setProposed] = useState<library.Extraction[]>([]);

  return (
    <section className="rn-card card-record rn-col kl-upload" aria-labelledby="kl-pick-title">
      <div className="rn-card-head">
        <h2 className="rn-card-title" id="kl-pick-title">
          What in “{source.title}” is a rule?
        </h2>
      </div>
      <p className="rn-muted">
        The document is stored and searchable. Select a passage below and press “Use the selected
        text”, or write the rule out yourself. Each one you send goes to review; none is in force
        until an approver accepts it.
      </p>
      <DraftWithPhibian
        sourceId={source.id}
        onDrafted={(drafts) => setProposed((p) => [...p, ...drafts])}
      />
      <div className="kl-excerpt" style={{ maxHeight: '22rem', overflowY: 'auto' }} tabIndex={0}>
        {text}
      </div>
      <div className="rn-row kl-actions">
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            const selected = window.getSelection()?.toString().trim() ?? '';
            if (selected !== '')
              setDraft((d) => ({ n: d.n + 1, initial: { statement: selected } }));
          }}
        >
          Use the selected text
        </Button>
      </div>

      <ProposeReading
        key={draft.n}
        source={source.id}
        initial={draft.initial}
        onProposed={(extraction) => setProposed((p) => [...p, extraction])}
      />

      {proposed.length > 0 ? (
        <div role="status">
          <p className="rn-strong">
            {proposed.length === 1 ? '1 reading' : `${proposed.length} readings`} sent for review
          </p>
          <ul>
            {proposed.map((e) => (
              <li key={e.id} className="rn-muted">
                {e.proposedStatement}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="rn-row kl-actions">
        <Button
          type="button"
          variant="primary"
          onClick={() => {
            onClose();
            navigate(`/library/sources/${source.id}`);
          }}
        >
          Done — open the document
        </Button>
      </div>
    </section>
  );
}

/** A rule or guidance typed by hand: stored as a short document, then proposed. */
function WriteRule({
  flavour,
  onBack,
  onClose,
}: {
  flavour: 'rule' | 'guidance';
  onBack: () => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');
  const [sent, setSent] = useState<string | null>(null);
  const guidance = flavour === 'guidance';

  return (
    <section className="rn-card card-record rn-col kl-upload" aria-labelledby="kl-write-title">
      <div className="rn-card-head">
        <h2 className="rn-card-title" id="kl-write-title">
          {guidance ? 'Add organizational guidance' : 'Write a policy or rule'}
        </h2>
        <button type="button" className="rn-btn-quiet" onClick={onBack}>
          Back
        </button>
      </div>
      <label className="rn-col kl-field">
        <span className="rn-strong">Title (optional)</span>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={guidance ? 'Documenting home visits' : 'Lone worker check-in'}
        />
        <span className="rn-muted">
          Stored as its own short document, so the rule keeps a source, a date and an author.
        </span>
      </label>
      <ProposeReading
        statementLabel={guidance ? 'The guidance' : 'The rule'}
        bindingChoices={
          guidance ? ['guidance', 'informational'] : ['hard_constraint', 'strong_default']
        }
        // The rule's own words are its document.
        source={async (statement) =>
          (
            await library.uploadSource({
              title: title.trim() || statement.slice(0, 80),
              sourceText: statement,
            })
          ).id
        }
        onProposed={(extraction) => {
          setSent(extraction.proposedStatement);
          setTitle('');
        }}
      />
      {sent !== null ? (
        <p role="status" className="rn-muted">
          Sent for review: “{sent}”. It is not in force until an approver accepts it.{' '}
          <button type="button" className="rn-link" onClick={onClose}>
            Close
          </button>
        </p>
      ) : null}
    </section>
  );
}
